import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  FileDown,
  Package,
  ShoppingBag,
  TrendingUp,
  DollarSign,
  AlertCircle,
  Wallet,
  CreditCard,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  payment_summary_report,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style";
import { retrieveData } from "../LocalConnection/LocalConnection";

const toApiDate = (d) => format(d, "yyyy-MM-dd");

const buildDateRange = (s, e) => {
  const start = new Date(s);
  start.setHours(0, 0, 0, 0);
  const end = new Date(e);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

// ── Excel helpers ─────────────────────────────────────────────────────────────
const HEADER_FILL = { fgColor: { rgb: "2D6A4F" } }; // dark green
const SUBTOT_FILL = { fgColor: { rgb: "D8F3DC" } }; // light green
const WHITE_FILL = { fgColor: { rgb: "FFFFFF" } };
const ALT_FILL = { fgColor: { rgb: "F0FFF4" } };

const bold = (color = "000000") => ({ bold: true, color: { rgb: color } });
const border = () => ({
  top: { style: "thin", color: { rgb: "CCCCCC" } },
  bottom: { style: "thin", color: { rgb: "CCCCCC" } },
  left: { style: "thin", color: { rgb: "CCCCCC" } },
  right: { style: "thin", color: { rgb: "CCCCCC" } },
});

const cellStyle = (fill, font, align = "left") => ({
  fill: { patternType: "solid", ...fill },
  font,
  border: border(),
  alignment: { horizontal: align, vertical: "center", wrapText: true },
});

export default function PaymentSummaryReport() {
  const { toast } = useToast();

  const [startCal, setStartCal] = useState(new Date());
  const [endCal, setEndCal] = useState(new Date());
  const [filterOption, setFilterOption] = useState("today");
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [paymentRows, setPaymentRows] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const restaurantName = retrieveData("restaurant_name");
  const [stats, setStats] = useState({
    totalItems: 0,
    totalOrders: 0,
    totalQuantity: 0,
    totalRevenue: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 10;

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchData = useCallback(
    async (startDt, endDt) => {
      if (startDt > endDt) {
        toast({
          title: "Validation Error",
          description: "Start date cannot be after end date.",
          variant: "destructive",
        });
        return;
      }

      setShowLoaderAdmin(true);

      const fd = new FormData();
      fd.append("start_date", toApiDate(startDt));
      fd.append("end_date", toApiDate(endDt));

      try {
        const res = await server_post_data(payment_summary_report, fd);

        if (res.data.error) {
          toast({
            title: "Error",
            description: res.data.message,
            variant: "destructive",
          });

          setPaymentRows([]);
          setPaymentSummary(null);
          return;
        }

        const d = res.data.data;

        // ✅ NEW DATA STRUCTURE
        const rows = d.rows || [];
        const summary = d.summary || {};

        // 🔹 Table Data
        setPaymentRows(rows);
        // 🔹 Summary Data (NEW STATE)
        setPaymentSummary(summary);
        // 🔹 Stats (optional)
        setStats({
          totalOrders: rows.length,
          totalCash: summary?.cash?.total || 0,
          totalCard: summary?.card?.total || 0,
          totalOther: summary?.other?.total || 0,
          totalZomato: summary?.zomato_wallet?.total || 0,
          totalSwiggy: summary?.swiggy_wallet?.total || 0,
        });

        setCurrentPage(1);
      } catch (e) {
        console.error(e);
        toast({
          title: "Error",
          description: "Network error occurred",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [toast],
  );

  // ── Filter ───────────────────────────────────────────────────────────────
  const handleFilterChange = useCallback(
    (sel) => {
      setFilterOption(sel);
      if (sel === "custom") return;
      const t = new Date();
      let s, e;
      if (sel === "today") {
        s = new Date(t);
        e = new Date(t);
      } else if (sel === "yesterday") {
        const y = new Date(t);
        y.setDate(y.getDate() - 1);
        s = new Date(y);
        e = new Date(y);
      } else if (sel === "this_week") {
        s = new Date(t);
        s.setDate(t.getDate() - t.getDay());
        e = new Date(t);
      } else if (sel === "this_month") {
        s = new Date(t.getFullYear(), t.getMonth(), 1);
        e = new Date(t.getFullYear(), t.getMonth() + 1, 0);
      } else if (sel === "this_year") {
        s = new Date(t.getFullYear(), 0, 1);
        e = new Date(t.getFullYear(), 11, 31);
      } else return;
      setStartCal(s);
      setEndCal(e);
      const { start, end } = buildDateRange(s, e);
      fetchData(start, end);
    },
    [fetchData],
  );

  const handleCustomFetch = useCallback(() => {
    if (!startCal || !endCal) return;
    const { start, end } = buildDateRange(startCal, endCal);
    fetchData(start, end);
  }, [startCal, endCal, fetchData]);

  // ── Excel Export (category-wise grouped) ─────────────────────────────────
  const exportPaymentToExcel = useCallback(() => {
    const sd = format(startCal, "yyyy-MM-dd");
    const ed = format(endCal, "yyyy-MM-dd");

    const rows = [];
    const styles = [];

    const push = (cells, cellStyles) => {
      rows.push(cells);
      styles.push(cellStyles);
    };

    // ── Meta ─────────────────────────────────────
    push([`Date: ${sd} to ${ed}`, "", "", "", "", "", ""], Array(7).fill({}));
    push(
      [`Restaurant: ${restaurantName}`, "", "", "", "", "", ""],
      Array(7).fill({}),
    );
    push(["", "", "", "", "", "", ""], Array(7).fill({}));

    // ── SUMMARY (Top like Excel) ─────────────────
    push(
      [
        "Total",
        paymentSummary?.cash?.total || 0,
        paymentSummary?.card?.total || 0,
        paymentSummary?.other?.total || 0,
        paymentSummary?.zomato_wallet?.total || 0,
        paymentSummary?.swiggy_wallet?.total || 0,
      ],
      Array(6).fill(cellStyle(SUBTOT_FILL, bold())),
    );

    push(
      [
        "Min",
        paymentSummary?.cash?.min || 0,
        paymentSummary?.card?.min || 0,
        paymentSummary?.other?.min || 0,
        paymentSummary?.zomato_wallet?.min || 0,
        paymentSummary?.swiggy_wallet?.min || 0,
      ],
      Array(6).fill(cellStyle(SUBTOT_FILL)),
    );

    push(
      [
        "Max",
        paymentSummary?.cash?.max || 0,
        paymentSummary?.card?.max || 0,
        paymentSummary?.other?.max || 0,
        paymentSummary?.zomato_wallet?.max || 0,
        paymentSummary?.swiggy_wallet?.max || 0,
      ],
      Array(6).fill(cellStyle(SUBTOT_FILL)),
    );

    push(
      [
        "Avg",
        paymentSummary?.cash?.avg || 0,
        paymentSummary?.card?.avg || 0,
        paymentSummary?.other?.avg || 0,
        paymentSummary?.zomato_wallet?.avg || 0,
        paymentSummary?.swiggy_wallet?.avg || 0,
      ],
      Array(6).fill(cellStyle(SUBTOT_FILL)),
    );

    push(["", "", "", "", "", "", ""], Array(7).fill({}));

    // ── HEADER ───────────────────────────────────
    push(
      [
        "Invoice Date",
        "Invoice No",        // ✅ NEW
        "Order ID",
        "Payment Method",    // ✅ NEW
        "Cash",
        "Card",
        "Other",
        "Zomato Wallet",
        "Swiggy Wallet",
      ],
      [
        cellStyle(HEADER_FILL, bold("FFFFFF")),
        cellStyle(HEADER_FILL, bold("FFFFFF")),
        cellStyle(HEADER_FILL, bold("FFFFFF")),
        cellStyle(HEADER_FILL, bold("FFFFFF")),
        cellStyle(HEADER_FILL, bold("FFFFFF")),
        cellStyle(HEADER_FILL, bold("FFFFFF")),
        cellStyle(HEADER_FILL, bold("FFFFFF")),
        cellStyle(HEADER_FILL, bold("FFFFFF")),
        cellStyle(HEADER_FILL, bold("FFFFFF")),
      ]
    );

    // ── DATA ROWS ────────────────────────────────
    paymentRows.forEach((row, idx) => {
      const fill = idx % 2 === 0 ? WHITE_FILL : ALT_FILL;

      push(
        [
          row.invoice_date,
          row.invoice_id, // ✅ NEW
          row.order_id,
          row.payment_mode,
          row.cash,
          row.card,
          row.other,
          row.zomato_wallet,
          row.swiggy_wallet,
        ],
        [
          cellStyle(fill),
          cellStyle(fill),
          cellStyle(fill, {}, "right"),
          cellStyle(fill, {}, "right"),
          cellStyle(fill, {}, "right"),
          cellStyle(fill, {}, "right"),
          cellStyle(fill, {}, "right"),
        ],
      );
    });

    // ── SHEET ────────────────────────────────────
    const ws = XLSX.utils.aoa_to_sheet(rows);

    ws["!cols"] = [
      { wch: 22 }, // Invoice Date
      { wch: 15 }, // Invoice No
      { wch: 12 }, // Order ID
      { wch: 20 }, // Payment Method
      { wch: 12 }, // Cash
      { wch: 12 }, // Card
      { wch: 12 }, // Other
      { wch: 18 }, // Zomato
      { wch: 18 }, // Swiggy
    ];

    // Apply styles
    styles.forEach((rowStyles, r) => {
      rowStyles.forEach((style, c) => {
        if (!style || !Object.keys(style).length) return;
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { v: "", t: "s" };
        ws[addr].s = style;
      });
    });

    const wb = {
      Sheets: { "Payment Report": ws },
      SheetNames: ["Payment Report"],
    };

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    FileSaver.saveAs(
      new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      }),
      `payment_summary_${sd}.xlsx`,
    );

    toast({
      title: "Success",
      description: "Payment report exported successfully",
    });
  }, [paymentRows, paymentSummary, startCal, endCal, restaurantName, toast]);



  // ── Pagination ────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(paymentRows.length / PER_PAGE);

  const currentData = paymentRows.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE,
  );

  const goPage = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };
  // ── Init ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const t = new Date();
    const s = new Date(t);
    s.setHours(0, 0, 0, 0);
    const e = new Date(t);
    e.setHours(23, 59, 59, 999);
    fetchData(s, e);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <MainLayout>
      <Header
        title="Payment Summary Report"
        subtitle="Analyze payment performance by individual menu items"
      />
      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            {
              label: "Total Orders",
              value: paymentRows.length,
              icon: <Package className="w-5 h-5 text-primary" />,
              bg: "bg-primary/10",
            },
            {
              label: "Total Cash",
              value: `₹${paymentSummary?.cash?.total || 0}`,
              icon: <DollarSign className="w-5 h-5 text-success" />,
              bg: "bg-success/10",
            },
            {
              label: "Total Card",
              value: `₹${paymentSummary?.card?.total || 0}`,
              icon: <CreditCard className="w-5 h-5 text-warning" />,
              bg: "bg-warning/10",
            },
            {
              label: "Total Other",
              value: `₹${paymentSummary?.other?.total || 0}`,
              icon: <Wallet className="w-5 h-5 text-purple-500" />,
              bg: "bg-purple-100",
            },
            {
              label: "Zomato Wallet",
              value: `₹${paymentSummary?.zomato_wallet?.total || 0}`,
              icon: <TrendingUp className="w-5 h-5 text-red-500" />,
              bg: "bg-red-100",
            },
            {
              label: "Swiggy Wallet",
              value: `₹${paymentSummary?.swiggy_wallet?.total || 0}`,
              icon: <TrendingUp className="w-5 h-5 text-orange-500" />,
              bg: "bg-orange-100",
            },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}
                >
                  {s.icon}
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {s.value}
                  </p>
                  <p className="text-sm text-muted-foreground">{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={handleFilterChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startCal ? (
                          format(startCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startCal}
                        onSelect={setStartCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endCal ? (
                          format(endCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endCal}
                        onSelect={setEndCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="flex items-end">
                  <Button
                    onClick={handleCustomFetch}
                    className="w-full md:w-auto"
                  >
                    Apply
                  </Button>
                </div>
              )}

              <div
                className={cn(
                  "flex items-end",
                  filterOption !== "custom" && "md:col-span-3",
                )}
              >
                <Button
                  variant="outline"
                  onClick={exportPaymentToExcel}
                  className="gap-2 w-full md:w-auto"
                  disabled={!paymentRows.length}
                >
                  <FileDown className="w-4 h-4" /> Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead >Sr.No</TableHead>
                <TableHead >Invoice Date</TableHead>
                <TableHead >Order ID</TableHead>
                <TableHead >Invoice No</TableHead>
                <TableHead >Payment Method</TableHead>
                <TableHead className="text-right">Cash</TableHead>
                <TableHead className="text-right">Card</TableHead>
                <TableHead className="text-right">Other</TableHead>
                <TableHead className="text-right">Zomato Wallet</TableHead>
                <TableHead className="text-right">Swiggy Wallet</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {showLoaderAdmin ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : currentData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center">
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                currentData.map((row, idx) => (
                  <TableRow key={idx} className="border-border">
                    {/* Sr No */}
                    <TableCell>
                      {(currentPage - 1) * PER_PAGE + idx + 1}
                    </TableCell>

                    {/* Date */}
                    <TableCell className="text-sm text-muted-foreground">
                      {row.invoice_date}
                    </TableCell>

                    {/* Order ID */}
                    <TableCell className="font-mono">#{row.order_id}</TableCell>
                    <TableCell className="font-mono">
  #{row.invoice_id}
</TableCell>
<TableCell>
{row.payment_mode}
</TableCell>
                    {/* Cash */}
                    <TableCell className="text-right font-semibold text-green-600">
                      ₹{row.cash || 0}
                    </TableCell>

                    {/* Card */}
                    <TableCell className="text-right font-semibold text-blue-600">
                      ₹{row.card || 0}
                    </TableCell>

                    {/* Other */}
                    <TableCell className="text-right font-semibold text-purple-600">
                      ₹{row.other || 0}
                    </TableCell>

                    {/* Zomato */}
                    <TableCell className="text-right font-semibold text-red-500">
                      ₹{row.zomato_wallet || 0}
                    </TableCell>

                    {/* Swiggy */}
                    <TableCell className="text-right font-semibold text-orange-500">
                      ₹{row.swiggy_wallet || 0}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              {/* 🔹 Left Info */}
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * PER_PAGE + 1} to{" "}
                {Math.min(currentPage * PER_PAGE, paymentRows.length)} of{" "}
                {paymentRows.length} entries
              </div>

              {/* 🔹 Pagination Buttons */}
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                {[...Array(totalPages)].map((_, i) => {
                  const p = i + 1;

                  if (
                    p === 1 ||
                    p === totalPages ||
                    (p >= currentPage - 1 && p <= currentPage + 1)
                  )
                    return (
                      <Button
                        key={p}
                        variant={p === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => goPage(p)}
                      >
                        {p}
                      </Button>
                    );

                  if (p === currentPage - 2 || p === currentPage + 2)
                    return (
                      <span key={p} className="px-1">
                        …
                      </span>
                    );

                  return null;
                })}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-primary flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Report Information:
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside ml-4">
                <li>
                  Report shows item-wise sales for the selected date range.
                </li>
                <li>Excel export is grouped by category with sub-totals.</li>
                <li>Restaurant #149 uses a 3 AM – 3 AM business day cycle.</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
